import React from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { AdminNavLinkConfig } from "../../config/adminNavigationConfig";

interface NestedNavItemProps {
  item: AdminNavLinkConfig;
  isExpanded: boolean;
  onToggle: () => void;
  userPermissions: string[];
  notificationSources: Record<string, number | undefined>;
  getNotificationCount: (
    item: AdminNavLinkConfig,
    sources: Record<string, number | undefined>,
  ) => number | undefined;
  onLinkClick: () => void;
  isCompact?: boolean;
}

const NestedNavItem: React.FC<NestedNavItemProps> = ({
  item,
  isExpanded,
  onToggle,
  userPermissions,
  notificationSources,
  getNotificationCount,
  onLinkClick,
  isCompact = false,
}) => {
  const { t } = useTranslation();

  // Filter children based on permissions
  const accessibleChildren = (item.children || []).filter((child) =>
    child.permission ? userPermissions.includes(child.permission) : true,
  );

  const hasChildren = accessibleChildren.length > 0;
  const notificationCount = getNotificationCount(item, notificationSources);

  // If no children, render as simple nav item
  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        onClick={onLinkClick}
        className={({ isActive }) =>
          cn(
            "relative flex items-center gap-2.5 rounded-md px-2 group sidebar-nav-item",
            isCompact ? "py-1 text-xs" : "py-1.5 text-sm",
            "hover:text-foreground",
            isActive
              ? "active bg-primary/10 text-primary font-medium"
              : "text-muted-foreground",
          )
        }
      >
        <span className="flex h-4 w-4 items-center justify-center">
          {item.icon}
        </span>
        <span className="flex-1 truncate">
          {t(item.textKey, item.defaultText)}
        </span>
        {typeof notificationCount === "number" && notificationCount > 0 && (
          <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {notificationCount.toString()}
          </span>
        )}
      </NavLink>
    );
  }

  // Render parent with expandable children
  return (
    <div className="relative">
      {/* Parent item row */}
      <div className="flex items-center">
        {/* Expand/collapse toggle */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded hover:bg-muted/50 transition-colors",
            "text-muted-foreground hover:text-foreground",
          )}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-200",
              isExpanded && "rotate-90",
            )}
          />
        </button>

        {/* Parent link */}
        <NavLink
          to={item.to}
          onClick={onLinkClick}
          className={({ isActive }) =>
            cn(
              "relative flex flex-1 items-center gap-2 rounded-md px-1.5 group sidebar-nav-item",
              isCompact ? "py-1 text-xs" : "py-1.5 text-sm",
              "hover:text-foreground",
              isActive
                ? "active bg-primary/10 text-primary font-medium"
                : "text-muted-foreground",
            )
          }
        >
          <span className="flex h-4 w-4 items-center justify-center">
            {item.icon}
          </span>
          <span className="flex-1 truncate">
            {t(item.textKey, item.defaultText)}
          </span>
          {typeof notificationCount === "number" && notificationCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
              {notificationCount.toString()}
            </span>
          )}
        </NavLink>
      </div>

      {/* Children with animation */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="relative ml-3 mt-0.5 pl-3 border-l border-border/50">
          {accessibleChildren.map((child) => {
            const childNotificationCount = getNotificationCount(
              child,
              notificationSources,
            );
            return (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={onLinkClick}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center gap-2 rounded-md px-2 group sidebar-nav-item",
                    isCompact ? "py-0.5 text-xs" : "py-1 text-xs",
                    "hover:text-foreground",
                    isActive
                      ? "active bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground/80",
                  )
                }
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center opacity-70">
                  {child.icon}
                </span>
                <span className="flex-1 truncate">
                  {t(child.textKey, child.defaultText)}
                </span>
                {typeof childNotificationCount === "number" &&
                  childNotificationCount > 0 && (
                    <span className="flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-primary/80 px-1 text-[9px] font-medium text-primary-foreground">
                      {childNotificationCount.toString()}
                    </span>
                  )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NestedNavItem;
